import React, { useState, useCallback, useEffect, useRef, useMemo, useContext } from "react";
import styled from "styled-components";
import { FaTimes, FaChevronDown } from 'react-icons/fa';
import AppContext from "../../context/store";

// Resimleri import ediyoruz
import odasistemi from '../../assets/images/oda-sistemi.png';
import iyzico from '../../assets/images/iyzico-banner.png';
import whatsapp from '../../assets/images/whatsapp.png';

const UpdateSlider = () => {
  const context = useContext(AppContext);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(() => {
    const savedState = localStorage.getItem('sliderState');
    return savedState === null ? true : JSON.parse(savedState);
  });
  const [isInitialized, setIsInitialized] = useState(false);

  const t = context?.t;
  const appName = process.env.APP_NAME;

  const slides = useMemo(() => [
    {
      type: 'whatsapp',
      title: t('dashboard.slider.whatsappSliderElement.title'),
      content: t('dashboard.slider.whatsappSliderElement.content'),
      image: whatsapp
    },
    {
      type: 'oda',
      title: t('dashboard.slider.sliderElement.title'),
      content: t('dashboard.slider.sliderElement.content'),
      image: odasistemi
    },
    {
      type: 'iyzico',
      title: t('dashboard.slider.iyzicoSliderElement.title'),
      content: t('dashboard.slider.iyzicoSliderElement.content'),
      image: iyzico
    },
  ], [t]);

  const totalSlides = slides.length;
  const sliderIntervalRef = useRef(null);
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchEndX, setTouchEndX] = useState(0);

  const toggleSlider = useCallback(() => {
    setIsOpen(prev => {
      const newState = !prev;
      localStorage.setItem('sliderState', JSON.stringify(newState));
      return newState;
    });
  }, []);

  const goToNextSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % totalSlides);
  }, [totalSlides]);

  const goToPrevSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  const goToSlide = useCallback((index) => {
    setCurrentIndex(index);
  }, []);

  const stopAutoSlide = useCallback(() => {
    if (sliderIntervalRef.current) {
      clearInterval(sliderIntervalRef.current);
      sliderIntervalRef.current = null;
    }
  }, []);

  const startAutoSlide = useCallback(() => {
    stopAutoSlide();
    sliderIntervalRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % totalSlides);
    }, 10000);
  }, [totalSlides, stopAutoSlide]);

  const handleTouchStart = useCallback((e) => {
    setTouchStartX(e.touches[0].clientX);
    stopAutoSlide();
  }, [stopAutoSlide]);

  const handleTouchEnd = useCallback((e) => {
    setTouchEndX(e.changedTouches[0].clientX);
  }, []);

  // Swipe handler
  useEffect(() => {
    if (touchEndX !== 0 && isInitialized) {
      const swipeThreshold = 50;
      const swipeDistance = touchEndX - touchStartX;

      if (swipeDistance > swipeThreshold) {
        goToPrevSlide();
      } else if (swipeDistance < -swipeThreshold) {
        goToNextSlide();
      }

      setTouchEndX(0);
      setTouchStartX(0);
      startAutoSlide();
    }
  }, [touchEndX, touchStartX, goToPrevSlide, goToNextSlide, startAutoSlide, isInitialized]);

  // İlk mount'ta auto slide başlat
  useEffect(() => {
    // Sadece bir kere çalışsın
    if (!isInitialized) {
      setIsInitialized(true);
      if (isOpen) {
        startAutoSlide();
      }
    }

    return () => {
      stopAutoSlide();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // isOpen değiştiğinde
  useEffect(() => {
    if (!isInitialized) return;

    if (isOpen) {
      startAutoSlide();
    } else {
      stopAutoSlide();
    }
  }, [isOpen, isInitialized, startAutoSlide, stopAutoSlide]);

  return (
    <SliderBodyOverlay>
      {isOpen ? (
        <SliderContainer>
          <NewBadge>{t(`appointments.detail.statusNew`)}</NewBadge>
          <CloseButton onClick={toggleSlider}>
            <FaTimes />
          </CloseButton>
          <SliderHeader>
            <SliderLogo>
              {t('dashboard.slider.sliderHeader')}
            </SliderLogo>
          </SliderHeader>

          {slides.map((slide, index) => (
            <SliderContent key={index} className={currentIndex === index ? 'active' : ''}>
              <SliderLeftContent>
                <SliderTimeline>{t('dashboard.slider.sliderDate')} &nbsp; — &nbsp; {appName === "salon"
                  ? <>SalonRandevu</>
                  : appName === "en"
                    ? <>EnRandevu</>
                    : <>Salon Management</>
                }</SliderTimeline>
                <SliderHeadline>
                  {slide.title}
                </SliderHeadline>
                <SliderTitle>
                  {slide.type === 'kariyer'
                    ? 'SALON KARİYER YAYINDA!'
                    : slide.type === 'whatsapp'
                      ? 'Fırsatlar'
                      : slide.type === 'oda' || slide.type === 'iyzico'
                        ? t('dashboard.slider.newFeatures')
                        : t('dashboard.slider.developmentFeatures')
                  }
                </SliderTitle>
                <SliderDescription dangerouslySetInnerHTML={{ __html: slide.content }} />

                {slide.type === 'linkgo' && (
                  <LinkGoButton
                    href="https://linkgo.tr/melisbeauty"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    LinkGo'yu Keşfet
                  </LinkGoButton>
                )}
              </SliderLeftContent>

              <SliderRightContent
                style={{ backgroundImage: `url(${slide.image})` }}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
              >
                <SliderToggler>
                  <SliderIndicators>
                    {slides.map((_, i) => (
                      <SliderIndicator
                        key={i}
                        onClick={() => {
                          stopAutoSlide();
                          goToSlide(i);
                          startAutoSlide();
                        }}
                        className={currentIndex === i ? 'active' : ''}
                      >
                        0{i + 1}
                      </SliderIndicator>
                    ))}
                  </SliderIndicators>
                </SliderToggler>
              </SliderRightContent>
            </SliderContent>
          ))}
        </SliderContainer>
      ) : (
        <ClosedSlider onClick={toggleSlider}>
          <FaChevronDown />
          <span>{t('dashboard.slider.openUpdates')}</span>
        </ClosedSlider>
      )}
    </SliderBodyOverlay>
  );
};

export default React.memo(UpdateSlider);

// Styled Components
const SliderBodyOverlay = styled.div`
  width: 100%;
  padding: 20px;
    
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const SliderContainer = styled.div`
  max-width: 95%;
  width: 100%;
  margin: 0 auto;
  background: #110725;
  color: #e8dff6;
  border-radius: 40px;
  box-shadow: 0 8px 15px rgba(0,0,0,0.15), 0 5px 5px rgba(0,0,0,0.1);
  overflow: hidden;
  font-family: 'Titillium Web', sans-serif;
  position: relative;
  transform: scale(0.98);
`;

const SliderHeader = styled.div`
  background: transparent;
  padding: 20px 35px;
  height: 65px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  position: relative;

  @media (max-width: 768px) {
    padding: 15px 20px;
    height: 55px;
  }
`;

const SliderLogo = styled.h2`
  font-size: 1.4em;
  width: 90%;
  letter-spacing: 1px;
  padding-top: 5px;
  color: #f7931e;
  font-weight: 700;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1.2em;
  }
`;

const SliderContent = styled.div`
  display: none;
  width: 100%;
  height: calc(100% - 65px);
  
  &.active {
    display: flex;
    animation: slideIn 0.5s forwards;
  }

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SliderLeftContent = styled.div`
  width: 35%;
  background: #110725;
  padding: 30px;
  min-height: 380px;
  position: relative;

  @media (max-width: 768px) {
    width: 100%;
    padding: 25px 20px;
    min-height: auto;
  }
`;

const SliderTimeline = styled.h6`
  font-size: 0.9em;
  letter-spacing: 1.5px;
  padding-top: 2%;
  font-weight: 300;
  transform: scaleY(0.9);
`;

const SliderHeadline = styled.h1`
  font-size: 3.3em;
  font-weight: 700;
  line-height: 1.1;
  margin: 15px 0;
  transform: scaleY(0.9);

  @media (max-width: 768px) {
    font-size: 2.5em;
    margin: 10px 0;
  }
`;

const SliderTitle = styled.h3`
  font-size: 1.5em;
  font-weight: 300;
  letter-spacing: 1.5px;
  line-height: 1.3;
  margin: 10px 0;
`;

const SliderDescription = styled.p`
  font-size: 1em;
  line-height: 1.5;
  margin: 15px 0;
  color: #FFF;
  text-align: justify;
  max-width: 95%;
  hyphens: auto;
  word-break: break-word;
  white-space: pre-line;
`;

const SliderRightContent = styled.div`
  width: 65%;
  background-size: cover;
  background-position: center;
  border-top-left-radius: 5vh;
  position: relative;
  min-height: 380px;

  @media (max-width: 768px) {
    width: 100%;
    height: 250px;
    min-height: auto;
    border-top-left-radius: 5vh;
  }
`;

const SliderToggler = styled.div`
  position: absolute;
  bottom: 30px;
  right: 30px;
  z-index: 5;
`;

const SliderIndicators = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #110725;
  padding: 4px;
  border-radius: 4px;
`;

const SliderIndicator = styled.div`
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  background: #110725;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin: 1px 0;
  position: relative;
  transition: all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  
  &.active {
    background: #f7931e;
    color: #fff;
    border-radius: 4px;
    z-index: 2;
  }
  
  &:hover:not(.active) {
    background: #2b1e47;
  }
`;

const NewBadge = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  background: #f7931e;
  color: white;
  padding: 5px 15px;
  padding-right: 25px;
  font-size: 14px;
  font-weight: bold;
  border-radius: 0 0 0 15px;
  z-index: 10;
  text-transform: uppercase;
  box-shadow: 0 2px 3px rgba(0,0,0,0.2);
`;

const LinkGoButton = styled.a`
  display: inline-block;
  background: linear-gradient(45deg, #f7931e, #ff6b35);
  border: none;
  border-radius: 25px;
  box-shadow: 0 4px 12px rgba(247, 147, 30, 0.3);
  color: #fff;
  font-size: 1em;
  font-weight: 600;
  letter-spacing: 0.5px;
  padding: 12px 30px;
  margin-top: 25px;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(247, 147, 30, 0.4);
    color: #fff;
    text-decoration: none;
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    font-size: 0.9em;
    padding: 10px 25px;
    margin-top: 20px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 36px;
  right: 15px;
  background: transparent;
  border: none;
  color: #f7931e;
  cursor: pointer;
  font-size: 20px;
  z-index: 11;
  padding: 5px;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1);
    color: #fff;
  }
`;

const ClosedSlider = styled.div`
  width: 100%;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  color: #666;
  transition: all 0.3s ease;

  &:hover {
    color: #333;
  }

  svg {
    font-size: 14px;
  }

  span {
    font-size: 14px;
  }
`;
